<script setup>
import { ref, onMounted } from "vue"
import NewsletterDialog from "@/components/dialogs/NewsletterDialog.vue"
import TableCellLoader from "@common/components/loaders/TableCellLoader.vue"

const dialogs = ref({ newsletter: { toggle: false } })
const email = ref("")
const loading = ref(true)

// Footer data arrays
const contactLinks = ref([
  { name: "Help", href: "#" },
  { name: "Start selling", href: "#" },
  { name: "About us", href: "#" },
  { name: "Blog", href: "#" },
])

const categories = ref([
  { name: "Solar Panels", href: "#" },
  { name: "Inverters", href: "#" },
  { name: "Energy Storage", href: "#" },
  { name: "Mounting Systems", href: "#" },
  { name: "Electro-technical", href: "#" },
  { name: "E-mobility", href: "#" },
])

const brands = ref([
  { name: "Jinko", href: "#" },
  { name: "JA Solar", href: "#" },
  { name: "Canadian", href: "#" },
  { name: "SolarEdge", href: "#" },
  { name: "BYD", href: "#" },
  { name: "Astronergy", href: "#" },
  { name: "LONGi", href: "#" },
  { name: "Trina", href: "#" },
  { name: "Huawei", href: "#" },
  { name: "Sungrow", href: "#" },
  { name: "PylonTech", href: "#" },
])

// Simulate data loading
onMounted(() => {
  setTimeout(() => {
    loading.value = false
  }, 5000) // 2 second loading simulation
})
</script>

<template>
  <footer class="container mt-4 mx-auto px-4 sm:px-6">
    <!-- Newsletter Section -->
    <div class="py-6 border-t border-gray-200">
      <div class="md:flex items-center justify-between">
        <div>
          <h2 class="text-2xl font-bold text-gray-900 mb-2">
            Subscribe to our newsletter
          </h2>
          <p class="text-gray-600 mb-6">
            Join our newsletter to stay up to date on features and releases
          </p>
        </div>

        <div class="flex w-full max-w-[460px] flex-col sm:flex-row gap-3">
          <input
            v-model="email"
            type="email"
            placeholder="Enter your email"
            class="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <button
            :disabled="!email"
            class="px-6 py-3 disabled:opacity-50 disabled:cursor-not-allowed bg-gray-800 text-white rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors duration-200 font-medium"
            @click="dialogs.newsletter.toggle = true"
          >
            Subscribe
          </button>
        </div>
      </div>
    </div>

    <!-- Main Footer Content -->
    <div class="border-t border-gray-200">
      <div class="grid py-12 grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-8">
        <!-- Company Info Section -->
        <div class="lg:col-span-2 col-span-full">
          <div class="flex space-x-3 items-center">
            <!-- Logo -->
            <router-link
              to="/"
              class="mb-4 inline-block text-gray-800 text-[29px] font-extrabold leading-tight tracking-tighter"
            >
              <span> L'Entrepôt </span>
            </router-link>
            <p class="flex items-center text-xs">
              Par
              <img
                class="h-4 w-4 mx-1"
                src="https://images.ctfassets.net/1b6f0wuf6ave/42P3RP32nltWjyhI0fg0PY/57029d3dbc531bad21cd75c871078436/logo_official_1.svg"
                alt="Company Icon"
              />
              <span class="font-extrabold leading-tight tracking-tighter">
                projetsolaire
              </span>
            </p>
          </div>

          <div class="mb-4 max-w-[380px] p-6 bg-[#f8f8f8] rounded-xl">
            <div class="flex items-center text-teal-600 mb-4">
              <svg class="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path
                  d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"
                ></path>
                <path
                  d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"
                ></path>
              </svg>
              serviceclient@projetsolaire.com
            </div>

            <div
              class="flex items-center font-bold px-3 py-1 rounded-full border-2 w-fit border-gray-300 text-black mb-4"
            >
              <svg
                class="w-4 h-3 mr-2 rounded-full"
                viewBox="0 0 21 15"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect width="21" height="15" fill="white" />
                <rect width="7" height="15" fill="#002395" />
                <rect x="14" width="7" height="15" fill="#ED2939" />
              </svg>
              +33 7 44 09 61 13
            </div>

            <div class="text-sm text-gray-500 mb-2">
              <p>Mon - Fri 09:00 - 17:00 CET</p>
              <p>Call price according to operator's rates.</p>
            </div>
          </div>

          <div class="text-xs text-gray-400 mt-6 leading-relaxed">
            <p>
              {{ $t("hardware-marketplace.shop.footer.legal-disclaimer") }}
            </p>
          </div>
        </div>

        <!-- Contact Section -->
        <div>
          <h3 class="font-semibold text-gray-800 mb-4">Contact</h3>
          <ul class="space-y-2 text-sm text-gray-600">
            <template v-if="loading">
              <li v-for="n in 4" :key="n" class="h-5">
                <TableCellLoader />
              </li>
            </template>
            <template v-else>
              <li v-for="link in contactLinks" :key="link.name">
                <a :href="link.href" class="hover:text-gray-800">{{
                  link.name
                }}</a>
              </li>
            </template>
          </ul>
        </div>

        <!-- Categories Section -->
        <div>
          <h3 class="font-semibold text-gray-800 mb-4">Categories</h3>
          <ul class="space-y-2 text-sm text-gray-600">
            <template v-if="loading">
              <li v-for="n in 6" :key="n" class="h-5">
                <TableCellLoader />
              </li>
            </template>
            <template v-else>
              <li v-for="category in categories" :key="category.name">
                <a :href="category.href" class="hover:text-gray-800">{{
                  category.name
                }}</a>
              </li>
            </template>
          </ul>
        </div>

        <!-- Brands Section -->
        <div>
          <h3 class="font-semibold text-gray-800 mb-4">Brands</h3>
          <ul class="space-y-2 text-sm text-gray-600">
            <template v-if="loading">
              <li v-for="n in 11" :key="n" class="h-5">
                <TableCellLoader />
              </li>
            </template>
            <template v-else>
              <li v-for="brand in brands" :key="brand.name">
                <a :href="brand.href" class="hover:text-gray-800">{{
                  brand.name
                }}</a>
              </li>
            </template>
          </ul>
        </div>
      </div>

      <!-- Bottom Section -->
      <div class="border-t border-gray-200 py-6">
        <div class="flex flex-col md:flex-row justify-between items-center">
          <!-- Copyright -->
          <div class="text-xs text-gray-500 mb-4 md:mb-0">
            {{ $t("footer.copyright") }}
          </div>

          <!-- Payment Methods -->
          <div class="flex w-[220px] gap-4 items-center space-x-4 mb-4 md:mb-0">
            <!-- Stripe -->
            <svg
              width="53"
              viewBox="0 0 29 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M2.0267 4.70227C2.0267 4.38985 2.28304 4.26969 2.70761 4.26969C3.31642 4.26969 4.08545 4.45394 4.69426 4.78238V2.89987C4.02937 2.63551 3.3725 2.53138 2.70761 2.53138C1.08144 2.53138 0 3.38051 0 4.7984C0 7.00935 3.04406 6.65688 3.04406 7.61015C3.04406 7.97864 2.72363 8.0988 2.27503 8.0988C1.61015 8.0988 0.761015 7.82644 0.0881175 7.45794V9.36449C0.833111 9.68491 1.58611 9.82109 2.27503 9.82109C3.94125 9.82109 5.08678 8.99599 5.08678 7.56208C5.07877 5.1749 2.0267 5.59947 2.0267 4.70227ZM7.44192 0.929239L5.48732 1.34579L5.47931 7.76235C5.47931 8.94793 6.36849 9.82109 7.55407 9.82109C8.21095 9.82109 8.69159 9.70093 8.95594 9.55674V7.93057C8.6996 8.03471 7.43391 8.4032 7.43391 7.21762V4.37383H8.95594V2.66756H7.43391L7.44192 0.929239ZM11.4473 3.26035L11.3191 2.66756H9.58879V9.6769H11.5915V4.92657C12.0641 4.30975 12.8652 4.4219 13.1135 4.51001V2.66756C12.8571 2.57143 11.9199 2.39519 11.4473 3.26035ZM13.6021 2.66756H15.6128V9.6769H13.6021V2.66756ZM13.6021 2.05874L15.6128 1.62617V0L13.6021 0.424566V2.05874ZM19.7944 2.53138C19.0093 2.53138 18.5047 2.89987 18.2243 3.15621L18.1202 2.65955H16.3578V12L18.3605 11.5754L18.3685 9.30841C18.6569 9.51669 19.0814 9.81308 19.7864 9.81308C21.2203 9.81308 22.526 8.65955 22.526 6.12016C22.518 3.79706 21.1963 2.53138 19.7944 2.53138ZM19.3137 8.05073C18.8411 8.05073 18.5607 7.88251 18.3685 7.67423L18.3605 4.70227C18.5688 4.46996 18.8571 4.30975 19.3137 4.30975C20.0427 4.30975 20.5474 5.12684 20.5474 6.17624C20.5474 7.24967 20.0507 8.05073 19.3137 8.05073ZM28.8384 6.20027C28.8384 4.14953 27.8451 2.53138 25.9466 2.53138C24.0401 2.53138 22.8865 4.14953 22.8865 6.18425C22.8865 8.59546 24.2483 9.81308 26.2029 9.81308C27.1562 9.81308 27.8772 9.5968 28.4219 9.29239V7.69025C27.8772 7.96262 27.2523 8.13084 26.4593 8.13084C25.6822 8.13084 24.9933 7.85848 24.9052 6.91322H28.8224C28.8224 6.80908 28.8384 6.39252 28.8384 6.20027ZM24.8812 5.43925C24.8812 4.53405 25.4339 4.15754 25.9386 4.15754C26.4272 4.15754 26.9479 4.53405 26.9479 5.43925H24.8812Z"
                fill="#6772E5"
              ></path>
            </svg>

            <!-- Visa -->
            <div
              class="svg-icon"
              style="--557ae948: auto; --77e93df5: 21px; --6dd128bd: undefined"
            >
              <span class="nuxt-icon"
                ><svg
                  width="53"
                  height="21"
                  viewBox="0 0 53 21"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g clip-path="url(#clip0_9_47)">
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M26.909 7.17944C26.8793 9.54057 28.9949 10.8582 30.5885 11.6416C32.2259 12.4455 32.7758 12.9609 32.7696 13.6797C32.7571 14.7799 31.4634 15.2653 30.2526 15.2842C28.1402 15.3172 26.9121 14.7089 25.9356 14.2487L25.1748 17.8407C26.1544 18.2963 27.9683 18.6935 29.8495 18.7108C34.2648 18.7108 37.1536 16.512 37.1693 13.1028C37.1865 8.77618 31.2369 8.53656 31.2775 6.60257C31.2915 6.01626 31.8462 5.39045 33.0617 5.23127C33.6632 5.15096 35.324 5.08942 37.2068 5.96416L37.9457 2.4888C36.9334 2.11677 35.6318 1.7605 34.0117 1.7605C29.8557 1.7605 26.9325 3.98923 26.909 7.17944ZM45.0469 2.05996C44.2407 2.05996 43.561 2.53446 43.2579 3.26263L36.9505 18.4555H41.3627L42.2407 16.0076H47.6326L48.1419 18.4555H52.0307L48.6371 2.05996H45.0469ZM45.664 6.48906L46.9373 12.6456H43.4501L45.664 6.48906ZM21.5594 2.05996L18.0816 18.4555H22.2859L25.7622 2.05996H21.5594ZM15.3396 2.05996L10.9633 13.2193L9.19305 3.73071C8.98531 2.67159 8.16505 2.05996 7.25412 2.05996H0.100029L0 2.53604C1.4687 2.85754 3.13732 3.37614 4.14815 3.93096C4.76679 4.26978 4.94344 4.56609 5.14649 5.37155L8.49935 18.4555H12.9429L19.7549 2.05996H15.3396Z"
                      fill="#141843"
                    ></path>
                  </g>
                  <defs>
                    <clipPath id="clip0_9_47">
                      <rect
                        width="53"
                        height="18"
                        fill="white"
                        transform="translate(0 1)"
                      ></rect>
                    </clipPath>
                  </defs>
                </svg>
              </span>
            </div>
            <!-- Mastercard -->
            <svg
              width="28"
              height="21"
              viewBox="0 0 28 21"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <mask
                id="mask0_9_48"
                style="mask-type: luminance"
                maskUnits="userSpaceOnUse"
                x="0"
                y="2"
                width="28"
                height="18"
              >
                <path d="M27.2948 2H0V19.0592H27.2948V2Z" fill="white"></path>
              </mask>
              <g mask="url(#mask0_9_48)">
                <path
                  d="M17.7342 3.90234H9.64844V17.1315H17.7342V3.90234Z"
                  fill="#FF5F00"
                ></path>
                <path
                  d="M10.4816 10.5171C10.4806 9.24324 10.7689 7.98586 11.3247 6.84009C11.8804 5.69433 12.689 4.6902 13.6894 3.90371C12.4504 2.92834 10.9625 2.32182 9.39572 2.15346C7.82891 1.9851 6.24643 2.2617 4.82913 2.95164C3.41183 3.64158 2.21689 4.71703 1.3809 6.05507C0.544896 7.39311 0.101562 8.93976 0.101562 10.5183C0.101562 12.0968 0.544896 13.6434 1.3809 14.9815C2.21689 16.3195 3.41183 17.3949 4.82913 18.0849C6.24643 18.7748 7.82891 19.0514 9.39572 18.8831C10.9625 18.7147 12.4504 18.1082 13.6894 17.1328C12.6887 16.3461 11.8799 15.3416 11.3241 14.1954C10.7684 13.0492 10.4803 11.7913 10.4816 10.5171Z"
                  fill="#EB001B"
                ></path>
                <path
                  d="M26.4773 15.7334V15.4623H26.594V15.4063H26.3164V15.4623H26.426V15.7334H26.4773ZM27.0162 15.7334V15.4063H26.9323L26.8343 15.64L26.7363 15.4063H26.6523V15.7334H26.713V15.4857L26.8039 15.6984H26.8669L26.9579 15.4857V15.7334H27.0162Z"
                  fill="#F79E1B"
                ></path>
                <path
                  d="M27.2804 10.5165C27.2804 12.0951 26.837 13.6419 26.0008 14.9799C25.1647 16.318 23.9696 17.3935 22.5521 18.0833C21.1346 18.7731 19.552 19.0496 17.9851 18.881C16.4182 18.7124 14.9302 18.1055 13.6914 17.1299C14.6913 16.3427 15.4997 15.3383 16.0556 14.1925C16.6115 13.0466 16.9003 11.7893 16.9003 10.5153C16.9003 9.24135 16.6115 7.98403 16.0556 6.8382C15.4997 5.69237 14.6913 4.68797 13.6914 3.90077C14.9302 2.9251 16.4182 2.31829 17.9851 2.14969C19.552 1.98109 21.1346 2.25751 22.5521 2.94735C23.9696 3.63719 25.1647 4.71262 26.0008 6.0507C26.837 7.38879 27.2804 8.93554 27.2804 10.5141V10.5165Z"
                  fill="#F79E1B"
                ></path>
              </g>
            </svg>
          </div>

          <!-- Legal Links -->
          <div class="flex space-x-4 text-xs text-gray-500">
            <router-link
              to="/help/guides/installers/legal/declaration-de-confidentialite"
              class="hover:text-gray-800 hover:underline"
              >{{ $t("footer.privacy-policy") }}</router-link
            >
            <span>|</span>
            <router-link
              to="/help/guides/installers/legal/mentions-legales"
              class="hover:text-gray-800 hover:underline"
              >{{ $t("footer.terms-and-conditions") }}</router-link
            >
          </div>
        </div>
      </div>
    </div>
    <NewsletterDialog
      v-if="dialogs.newsletter.toggle"
      v-model="dialogs.newsletter.toggle"
      :email="email"
    />
  </footer>
</template>
